import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, ChevronRight, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { usePricing, aiExtensions } from "@/lib/pricing";

export default function AiExpiryBanner() {
  const { user } = useAuth();
  const pricing = usePricing();
  const [dismissed, setDismissed] = useState(false);
  const [paying, setPaying] = useState(false);
  const days = user?.ai_days_left;
  const show =
    user && user.role !== "admin" && user.ai_active && days != null && days >= 1 && days <= 5 && !dismissed;

  const renew = async () => {
    const plan = user?.license_plan === "team" ? "team" : "einzel";
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "ai_extension",
        plan,
        days: aiExtensions(pricing, plan)[0].days,
        origin_url: window.location.origin,
        cancel_path: window.location.pathname,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setPaying(false);
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.3 }}
          data-testid="ai-expiry-banner"
          className="mb-4 flex items-center gap-3 rounded-xl border border-amber-400/30 bg-amber-400/[0.08] px-4 py-2.5"
        >
          <Clock className="h-4 w-4 text-amber-300 flex-shrink-0" />
          <p className="text-sm text-foreground/85 flex-1 min-w-0">
            Ihr KI-Zugang läuft in <strong className="text-amber-200">{days} {days === 1 ? "Tag" : "Tagen"}</strong> ab.
          </p>
          <button
            onClick={renew}
            disabled={paying}
            data-testid="ai-expiry-renew"
            className="inline-flex items-center gap-1 px-3.5 py-1.5 rounded-full bg-gold text-[#0a0d12] text-xs font-semibold hover:shadow-[0_6px_20px_-6px_rgba(212,175,55,0.7)] transition-all flex-shrink-0 disabled:opacity-60"
          >
            {paying ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <>Jetzt verlängern <ChevronRight className="h-3.5 w-3.5" /></>
            )}
          </button>
          <button
            onClick={() => setDismissed(true)}
            data-testid="ai-expiry-dismiss"
            aria-label="Hinweis schliessen"
            className="h-6 w-6 flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors flex-shrink-0"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </motion.div>
      )}
      <InvoiceOrderDialog
        open={invoiceOpen}
        onOpenChange={setInvoiceOpen}
        mode="ai_extension"
        days={aiExtensions(pricing, "team")[0].days}
      />
    </AnimatePresence>
  );
}
